<script setup>
import { computed } from 'vue';

const props = defineProps({
  levelYear: { type: Number, default: 5 }, 
  userRole: { type: String, default: 'student' }, 
  studentLevel: { type: Number, default: 4 },
  schulType: { type: String, default: 'E' }
});

// schulType에 따른 학교 분류 표시
const schoolTypeText = computed(() => {
  return props.schulType === 'M' ? '중학교' : '초등학교';
});

const levelDescriptions = {
  4: "’일상생활과 교과학습에 기반이 되는 기초 소양으로서 AI·디지털 기술에 대한 이해를 바탕으로 문제를 해결하고, AI·디지털 기술을 책임감 있고 올바르게 활용하고 평가하는 능력’ 을 충분히 갖춘 수준",
  3: "’일상생활과 교과학습에 기반이 되는 기초 소양으로서 AI·디지털 기술에 대한 이해를 바탕으로 문제를 해결하고, AI·디지털 기술을 책임감 있고 올바르게 활용하고 평가하는 능력’ 을 적절히 갖춘 수준",
  2: "’일상생활과 교과학습에 기반이 되는 기초 소양으로서 AI·디지털 기술에 대한 이해를 바탕으로 문제를 해결하고, AI·디지털 기술을 책임감 있고 올바르게 활용하고 평가하는 능력’ 을 최소한으로 갖춘 수준",
  1: "’일상생활과 교과학습에 기반이 되는 기초 소양으로서 AI·디지털 기술에 대한 이해를 바탕으로 문제를 해결하고, AI·디지털 기술을 책임감 있고 올바르게 활용하고 평가하는 능력’ 이 부족한 수준"
};

const achievementData = {
  student: {
    E: { // 초등
      4: ['AI 원리 및 데이터 분석을 깊이 이해하고 AI의 결과물을 비판적으로 평가할 수 있다.', 'AI 도구의 장단점을 평가하여 문제 해결과 창작에 창의적으로 적용할 수 있다.', 'AI 안전 및 윤리 규칙을 이해하고 사회적 영향을 조사하거나 책임 있는 사용을 적극 실천할 수 있다.', '인간과 AI의 인지 과정을 비교 분석하며 지속 가능한 문제 정의에 주체적으로 참여할 수 있다.'],
      3: ['기계학습을 체험하고 AI 사례를 구별하며 생성형 AI의 종류와 활용 사례를 설명할 수 있다.', 'AI 도구 활용 문제 해결 과정을 탐색하고 AI 콘텐츠 제작을 체험하며 장단점을 구별할 수 있다.', 'AI의 안전한 사용을 실천하고 개인정보 보호의 중요성을 설명하며 AI의 이로움과 해로움을 구별할 수 있다.', 'AI가 잘하는 일을 발견하고 AI와 협업하는 자세를 인식하며 지속 가능한 AI 활용 사례를 탐색할 수 있다.'],
      2: ['생활 속 AI의 원리와 기계학습의 간단한 개념을 탐색할 수 있다.', 'AI 도구의 활용 사례를 탐색하고 가장 기본적인 창작 사례를 찾아 설명할 수 있다.', '안전한 AI 사용을 위한 기본 규칙과 개인정보 보호의 중요성을 단순하게 설명할 수 있다.', '지속 가능한 사회를 위한 AI의 활용 사례를 탐색하고 AI가 잘하는 일을 구체적으로 발견할 수 있다.'],
      1: ['AI 개념에 대한 이해와 AI인 것을 구별하는 능력을 키울 수 있다.', 'AI 도구 활용의 필요성을 인식하고 학습 및 창작 도구 사용 경험을 쌓을 수 있다.', '안전한 AI를 위한 기본 규칙과 개인정보 보호의 중요성에 대한 인식을 확립할 수 있다.', 'AI의 역할과 인간과의 협업 개념에 대한 호기심과 탐구 의지를 키울 수 있다.']
    },
    M: { // 중등
      4: ['간단한 기계학습 모델을 직접 구현할 수 있고, 일상생활과 학습 상황에서 적절한 AI·디지털 도구를 선택하여 문제를 효과적으로 해결할 수 있다.', 'AI·디지털 도구 사용 과정에서 발생할 수 있는 윤리적 문제를 식별하고 예방 및 해결 방안을 탐색할 수 있다.', '지속가능한 사회를 위해 AI를 활용하여 주체적인 태도로 창의적 문제해결 방안을 구안하고 적용할 수 있다.'],
      3: ['기계학습의 개념과 종류, 원리를 설명하고, 생성형 인공지능과 다른 인공지능의 차이를 설명할 수 있다.', '실생활에서 AI·디지털 도구를 활용하여 문제를 해결하며, 인공지능의 사회적 영향력과 윤리적 쟁점을 다양한 관점에서 탐색할 수 있다.', '지속 가능한 사회를 위해 AI를 활용한 해결 방안을 구상하고 AI와의 협업 과정에서 인간의 주체적인 역할을 탐색할 수 있다.'],
      2: ['기계학습과 생성형 AI의 기본 개념을 설명할 수 있다.', 'AI·디지털 도구를 문제 해결과 창작 활동에 활용하여 다양한 결과물을 제작할 수 있다.', 'AI·디지털 도구 사용 과정에서 발생할 수 있는 윤리적 이슈를 설명할 수 있다.', '지속 가능한 발전을 위해 AI로 간단한 해결 방안을 탐색하고 AI와 협업을 시도할 수 있다.'],
      1: ['AI에 대한 기본 이해를 바탕으로 AI가 하는 일을 구분하는 능력을 키울수 있다.', '실생활에서 문제해결을 위해 필요한 AI·디지털 도구를 탐색하고 적용해 볼 수 있다.', 'AI·디지털 기술 오남용의 위험성을 이해하고, AI 윤리 원칙의 중요성을 인식할 수 있다.', '인공지능과 협업을 시도하고 이 과정에서 인간이 잘하는 일과 AI가 잘하는 일을 발견할 수 있다.']
    }
  },
  parent: {
    4: ['AI 원리 및 데이터 분석을 깊이 이해하고 AI의 결과물을 비판적으로 평가할 수 있다.', 'AI 도구의 장단점을 평가하여 문제 해결과 창작에 창의적으로 적용할 수 있다.', 'AI 안전 및 윤리 규칙을 이해하고 사회적 영향을 조사하거나 책임 있는 사용을 적극 실천할 수 있다.', '인간과 AI의 인지 과정을 비교 분석하며 지속 가능한 문제 정의에 주체적으로 참여할 수 있다.'],
    3: ['기계학습을 체험하고 AI 사례를 구별하며 생성형 AI의 종류와 활용 사례를 설명할 수 있다.', 'AI 도구 활용 문제 해결 과정을 탐색하고 AI 콘텐츠 제작을 체험하며 장단점을 구별할 수 있다.', 'AI의 안전한 사용을 실천하고 개인정보 보호의 중요성을 설명하며 AI의 이로움과 해로움을 구별할 수 있다.', 'AI가 잘하는 일을 발견하고 AI와 협업하는 자세를 인식하며 지속 가능한 AI 활용 사례를 탐색할 수 있다.'],
    2: ['생활 속 AI의 원리와 기계학습의 간단한 개념을 탐색할 수 있다.', 'AI 도구의 활용 사례를 탐색하고 가장 기본적인 창작 사례를 찾아 설명할 수 있다.', '안전한 AI 사용을 위한 기본 규칙과 개인정보 보호의 중요성을 단순하게 설명할 수 있다.', '지속 가능한 사회를 위한 AI의 활용 사례를 탐색하고 AI가 잘하는 일을 구체적으로 발견할 수 있다.'],
    1: ['AI 개념에 대한 이해와 AI인 것을 구별하는 능력을 키울 수 있다.', 'AI 도구 활용의 필요성을 인식하고 학습 및 창작 도구 사용 경험을 쌓을 수 있다.', '안전한 AI를 위한 기본 규칙과 개인정보 보호의 중요성에 대한 인식을 확립할 수 있다.', 'AI의 역할과 인간과의 협업 개념에 대한 호기심과 탐구 의지를 키울 수 있다.']
  }
};
const descriptionRows = computed(() => {
  return [4, 3, 2, 1].map(lv => ({
    level: lv,
    desc: levelDescriptions[lv]
  }));
});


const tableRows = computed(() => {
  if (props.userRole === 'principal') {
    return [4, 3, 2, 1].map(lv => ({ level: lv, desc: levelDescriptions[lv] }));
  }  
  
  if (props.userRole === 'teacher') {
    return [4, 3, 2, 1].map(lv => ({ 
      level: lv, 
      achievements: achievementData.student[props.schulType.toUpperCase()]?.[lv] || [] 
    }));
  }
  
  const roleKey = props.userRole === 'parent' ? 'parent' : 'student';
  const lv = Number(props.studentLevel);
  const schulTypeUpper = props.schulType.toUpperCase(); // 대문자로 변환
  let achievements = [];
  
  if (roleKey === 'parent') {
    achievements = achievementData.parent[lv] || [];
  } else {
    achievements = achievementData.student[schulTypeUpper]?.[lv] || [];
  }
  
  return [{ 
    level: lv, 
    achievements: achievements,
    desc: levelDescriptions[lv] 
  }];
});

const isSingleView = computed(() => props.userRole === 'student' || props.userRole === 'parent');
const currentDesc = computed(() => levelDescriptions[Number(props.studentLevel)] || '');
</script>

<template>
  <div class="card" :class="props.userRole">
    
    <template v-if="isSingleView"> 
      <h5 class="card-title mt-9">나의 수준에서 기대되는 성취 수행</h5>   
      <p class="msg-info pt-4">
        수준별 기대 성취 수행에 기술된 내용은 학생이 확률적으로 수행할 것으로 기대되는 내용을 제시한 것이며, 각 학생이 실제로 성취 수준에 있는 내용을 수행한다는 것을 의미하지는 않습니다.
      </p> 
    </template>
    <template v-else-if="props.userRole === 'teacher'">
      <h5 class="card-title">{{schoolTypeText}} {{props.levelYear}}학년 수준별 AI·디지털 리터러시 기대 성취 수행</h5>
      <p class="msg-info py-2">AI·디지털 리터리시 검사에서 성취수준은 4개의 수준으로 구성되는데, 4수준이 가장 높은 수준을 나타냅니다.</p>
    </template>
    <template v-else-if="props.userRole === 'principal'">
      <h5 class="card-title">성취 수준</h5>
      <p class="msg-info py-2">AI·디지털 리터러시 검사의 성취 수준별 상세 설명입니다.</p>
    </template>

    <div class="ai-table step-table primary" :class="{ 'mt-4 single-view': isSingleView }">
      <div class="row thead">
        <div class="th">수준</div>
        <div class="td" v-if="props.userRole === 'principal'">수준 설명</div>
        <div class="td" v-else>기대 성취 수행</div>      
      </div>
      <div class="row" v-for="row in tableRows" :key="row.level">
        <div class="th"><span class="step">{{ row.level }}수준</span></div>
        <div class="td">
          <template v-if="props.userRole === 'principal'">
            {{ row.desc }}
          </template>
          <template v-else>
            <p v-for="(achievement, index) in row.achievements" :key="index">
              {{ achievement }}
            </p>
          </template>
        </div>
      </div>
    </div> 

    <template v-if="isSingleView">
      

      <div class="pt-8 student-view">
        <h5 class="card-title">성취 수준</h5>         
        <p class="msg-info py-4">        
          AI·디지털 리터러시 검사에서 성취 수준은 4개의 수준으로 구성되는데, 4수준이 가장 높은 수준을 나타냅니다.
        </p>     
        
        <div class="ai-table primary">
          <div class="row thead">
            <div class="th">수준</div>
            <div class="td">수준 설명</div>
          </div>
          <div class="row" v-for="row in descriptionRows" :key="row.level">
            <div class="th">
              <span class="step">{{ row.level }}수준</span> 
            </div>
            <div class="td">
              <p>{{ row.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </template>
    </div>
</template>

<style lang="scss" scoped>
@use "sass:list";
$ai-levels: (
  var(--result-ai-primary-90),
  var(--result-ai-primary-80),  
  var(--result-ai-primary-70),
  var(--result-ai-primary-60), 
);
.ai-table {  
  .row {
    > div {
      padding: 1rem 0 1rem 1rem;
      .step {
        font-weight: 600;         
        border-radius: 2.25rem;
        padding: .625rem 2rem;
        line-height: 1.6;
      }    
    }   
  } 
  @for $i from 2 through 5 {
    .row {
      &:nth-child(#{$i}) {
        .step {
          background-color: list.nth($ai-levels, $i - 1);        
        }
      }
    }    
  }   
  &.step-table {    
    .td {
      text-align: justify;
      word-break: keep-all;
      text-justify: inter-word;
      p {
        position: relative;
        padding-left: 15px;
        margin-bottom: .25rem;
        line-height: 1.3;
        &:last-child { margin-bottom: 0; }
        &::before {
          content: '';
          position: absolute;
          left: 0;
          top: 11px;
          width: 3px;
          height: 3px;
          background-color: var(--result-ai-blak);
          border-radius: 50%;       
        }
      }
    }
    border-bottom: 0;
  } 
  &.single-view.step-table .row > div p { line-height: 1.7; }
}
.student-view .row > div { 
  padding: 1.75rem 1.25rem; 
  line-height: 1.5;
  &.td {
    text-align: justify;
    word-break: keep-all;
    text-justify: inter-word;
  }
}

.msg-info {
  text-align: justify;
  word-break: keep-all;
  text-justify: inter-word;
}
</style>